/**
 * Healthcare API Service
 * 
 * API service for Google Cloud Healthcare (FHIR/DICOM) operations.
 */

import apiClient from './client';

// ==================== Types ====================

export interface FHIRPatientRecord {
  uuid: string;
  fhir_patient_id: string;
  record_type: 'clinic' | 'professional';
  clinic: number | null;
  clinic_name: string | null;
  professional: number | null;
  professional_name: string | null;
  patient_profile: number | null;
  patient_name: string;
  patient_identifier: string;
  patient_birth_date: string | null;
  patient_gender: string;
  patient_phone: string;
  patient_email: string;
  emergency_contacts: EmergencyContact[];
  patient_marital_status: string;
  patient_address_line: string;
  patient_city: string;
  patient_country: string;
  encounter_count: number;
  last_encounter_date: string | null;
  created_at: string;
  updated_at: string;
}

export interface FHIRPatientRecordDetail extends FHIRPatientRecord {
  encounters: FHIREncounterRecord[];
  dicom_studies: DICOMStudyRecord[];
}

export interface FHIREncounterRecord {
  uuid: string;
  fhir_encounter_id: string;
  encounter_date: string;
  encounter_type: string;
  reason: string;
  status: string;
  attending_professional: number | null;
  attending_professional_name: string | null;
  appointment: number | null;
  created_at: string;
}

export interface DICOMStudyRecord {
  uuid: string;
  study_instance_uid: string;
  study_date: string;
  study_description: string;
  modality: string;
  modality_display: string;
  series_count: number;
  instance_count: number;
  patient_name: string;
  referring_professional: number | null;
  created_at: string;
}

export interface EmergencyContact {
  name: string;
  phone: string;
  relationship: string;
}

export interface CreatePatientData {
  given_name: string;
  family_name: string;
  birth_date: string;
  gender: 'male' | 'female' | 'other' | 'unknown';
  identifier_value: string;
  identifier_type?: 'PASSPORT' | 'ID' | 'SSN';
  phone?: string;
  email?: string;
  address_line?: string;
  city?: string;
  country?: string;
  emergency_contacts?: EmergencyContact[];
  marital_status?: string;
  clinic_id?: number;
  professional_id?: number;
  patient_profile_id?: number;
  /** Client-generated id for idempotent offline replay -- see frontend/web/src/offline */
  client_uuid?: string;
}

export interface UpdatePatientData {
  given_name?: string;
  family_name?: string;
  phone?: string;
  email?: string;
  birth_date?: string;
  gender?: string;
  address_line?: string;
  city?: string;
  country?: string;
  emergency_contacts?: EmergencyContact[];
  marital_status?: string;
}

export interface CreateEncounterData {
  patient_record_uuid: string;
  encounter_type?: 'AMB' | 'EMER' | 'IMP' | 'VR';
  reason?: string;
  start_datetime?: string;
  attending_professional_id?: number;
  appointment_id?: number;
  // Consultation detail fields
  consult_type?: 'primera-consulta' | 'consulta-normal' | 'seguimiento';
  symptoms?: string | { description: string; duration?: string; severity?: string }[];
  hea?: string;
  physical_exam?: string;
  diagnosis?: string;
  diagnosis_icd?: string;
  treatment?: string;
  treatments?: { type: string; description: string; instructions?: string; duration?: string }[];
  funciones_organicas?: string;
  antecedentes?: string | { personales?: any; familiares?: any };
  evolution?: string;
  notes?: string;
  medications_adherence?: string;
  treatment_response?: string;
  next_follow_up?: string;
  /** Client-generated id for idempotent offline replay -- see frontend/web/src/offline */
  client_uuid?: string;
}

export interface VitalSignsData {
  patient_record_uuid: string;
  encounter_uuid?: string;
  heart_rate?: number;
  systolic_bp?: number;
  diastolic_bp?: number;
  temperature?: number;
  respiratory_rate?: number;
  oxygen_saturation?: number;
  weight?: number;
  height?: number;
  notes?: string;
}

export interface CreateObservationData {
  patient_record_uuid: string;
  encounter_uuid?: string;
  code: string;
  code_system?: string;
  code_display: string;
  value: string;
  value_type?: 'string' | 'quantity' | 'boolean' | 'codeableConcept';
  unit?: string;
  unit_code?: string;
  category?: 'vital-signs' | 'laboratory' | 'imaging' | 'exam';
  effective_datetime?: string;
  notes?: string;
}

export interface CreateConditionData {
  patient_record_uuid: string;
  encounter_uuid?: string;
  code: string;
  code_display: string;
  clinical_status?: 'active' | 'recurrence' | 'relapse' | 'inactive' | 'remission' | 'resolved';
  verification_status?: 'unconfirmed' | 'provisional' | 'differential' | 'confirmed' | 'refuted' | 'entered-in-error';
  onset_datetime?: string;
  notes?: string;
}

export interface CreateDocumentData {
  patient_record_uuid: string;
  encounter_uuid?: string;
  document_type: 'clinical-note' | 'discharge-summary' | 'history-and-physical' |
                 'progress-note' | 'consultation-note' | 'operative-note' |
                 'radiology-report' | 'pathology-report' | 'laboratory-report';
  title: string;
  content_type?: 'text/plain' | 'application/pdf' | 'image/jpeg' | 'image/png';
  description?: string;
  content_text?: string;
  content_base64?: string;
  content_url?: string;
  /** When set, uploaded as multipart to GCLOUD_IMAGES_BUCKET instead of being
   * base64-embedded - content_base64 is ignored if this is present. */
  file?: File;
}

export interface CreateAllergyData {
  patient_record_uuid: string;
  code: string;
  code_display: string;
  clinical_status?: 'active' | 'inactive' | 'resolved';
  verification_status?: 'unconfirmed' | 'confirmed' | 'refuted' | 'entered-in-error';
  category?: 'food' | 'medication' | 'environment' | 'biologic';
  criticality?: 'low' | 'high' | 'unable-to-assess';
  reaction_description?: string;
  onset_datetime?: string;
  notes?: string;
}

export interface CreateFamilyHistoryData {
  patient_record_uuid: string;
  relationship: string;
  relationship_display: string;
  condition_code?: string;
  condition_display: string;
  outcome?: string;
  age_of_onset?: string;
  deceased?: boolean;
  notes?: string;
}

export interface CreateProcedureData {
  patient_record_uuid: string;
  encounter_uuid?: string;
  code: string;
  code_display: string;
  status?: 'preparation' | 'in-progress' | 'completed' | 'cancelled';
  performed_datetime?: string;
  performer_name?: string;
  body_site?: string;
  outcome?: string;
  notes?: string;
}

export interface CreateCarePlanData {
  patient_record_uuid: string;
  encounter_uuid?: string;
  title: string;
  description?: string;
  status?: 'draft' | 'active' | 'completed' | 'cancelled';
  intent?: 'proposal' | 'plan' | 'order' | 'option';
  category?: string;
  activities?: Array<{
    activity_type: 'medicamento' | 'procedimiento' | 'indicacion' | 'referencia';
    description: string;
    status?: 'not-started' | 'scheduled' | 'in-progress' | 'completed' | 'cancelled';
  }>;
  notes?: string;
}

export interface CreateClinicalImpressionData {
  patient_record_uuid: string;
  encounter_uuid?: string;
  status?: 'in-progress' | 'completed' | 'entered-in-error';
  description: string;
  summary?: string;
  finding_codes?: string[];
  prognosis?: string;
  notes?: string;
}

export interface CreateMedicationStatementData {
  patient_record_uuid: string;
  encounter_uuid?: string;
  medication_code: string;
  medication_display: string;
  status?: 'active' | 'completed' | 'entered-in-error' | 'intended' | 'stopped' | 'on-hold' | 'unknown' | 'not-taken';
  dosage?: string;
  route?: string;
  frequency?: string;
  adherence?: 'taking' | 'not-taking' | 'unknown';
  reason?: string;
  effective_datetime?: string;
  notes?: string;
}

export interface PatientSearchParams {
  name?: string;
  identifier?: string;
  birth_date?: string;
  clinic_id?: number;
  professional_id?: number;
}

export interface HealthcareStats {
  total_patients: number;
  total_encounters: number;
  total_dicom_studies: number;
  recent_patients: FHIRPatientRecord[];
  patients_by_month: { month: string; count: number }[];
}

export interface FHIRResource {
  resourceType: string;
  id: string;
  [key: string]: any;
}

// ==================== API Functions ====================

const HEALTHCARE_BASE = '/healthcare';

/**
 * Get patient records with optional filters
 */
export async function getPatientRecords(params?: {
  clinic_id?: number;
  professional_id?: number;
  search?: string;
}): Promise<FHIRPatientRecord[]> {
  const response = await apiClient.get(`${HEALTHCARE_BASE}/patients/`, { params });
  // Handle both paginated response and direct array
  return Array.isArray(response.data) ? response.data : (response.data.results || []);
}

/**
 * Get a single patient record with details
 */
export async function getPatientRecord(uuid: string): Promise<FHIRPatientRecordDetail> {
  const response = await apiClient.get(`${HEALTHCARE_BASE}/patients/${uuid}/`);
  return response.data;
}

/**
 * Create a new FHIR patient
 */
export async function createPatient(data: CreatePatientData): Promise<FHIRPatientRecord> {
  const response = await apiClient.post(`${HEALTHCARE_BASE}/patients/`, data);
  return response.data;
}

/**
 * Update a patient record
 */
export async function updatePatient(uuid: string, data: UpdatePatientData): Promise<FHIRPatientRecord> {
  const response = await apiClient.patch(`${HEALTHCARE_BASE}/patients/${uuid}/`, data);
  return response.data;
}

/**
 * Delete a patient record
 */
export async function deletePatient(uuid: string): Promise<void> {
  await apiClient.delete(`${HEALTHCARE_BASE}/patients/${uuid}/`);
}

/**
 * Get full patient record from FHIR ($everything operation)
 */
export async function getPatientFullRecord(uuid: string): Promise<{
  local_record: FHIRPatientRecordDetail;
  fhir_resources: Record<string, FHIRResource[]>;
}> {
  const response = await apiClient.get(`${HEALTHCARE_BASE}/patients/${uuid}/full_record/`);
  return response.data;
}

/**
 * Search for patients
 */
export async function searchPatients(params: PatientSearchParams): Promise<{
  count: number;
  patients: any[];
}> {
  const response = await apiClient.post(`${HEALTHCARE_BASE}/search/`, params);
  return response.data;
}

// ==================== Encounters ====================

/**
 * Get encounters for a patient
 */
export async function getPatientEncounters(patientUuid: string): Promise<{
  count: number;
  encounters: FHIRResource[];
}> {
  const response = await apiClient.get(`${HEALTHCARE_BASE}/patients/${patientUuid}/encounters/`);
  return response.data;
}

/**
 * Create an encounter for a patient
 */
export async function createEncounter(data: CreateEncounterData): Promise<FHIREncounterRecord> {
  const response = await apiClient.post(
    `${HEALTHCARE_BASE}/patients/${data.patient_record_uuid}/create_encounter/`,
    data
  );
  return response.data;
}

/**
 * Update an encounter's status (e.g., mark as completed)
 */
export async function updateEncounterStatus(
  patientUuid: string,
  encounterId: string,
  status: 'in-progress' | 'finished' | 'cancelled'
): Promise<{ success: boolean; encounter: FHIRResource }> {
  const response = await apiClient.patch(
    `${HEALTHCARE_BASE}/patients/${patientUuid}/encounters/${encounterId}/status/`,
    { status }
  );
  return response.data;
}

// ==================== Observations & Vital Signs ====================

/**
 * Get observations for a patient
 */
export async function getPatientObservations(
  patientUuid: string, 
  category?: string
): Promise<{
  count: number;
  observations: FHIRResource[];
}> {
  const params = category ? { category } : undefined;
  const response = await apiClient.get(
    `${HEALTHCARE_BASE}/patients/${patientUuid}/observations/`,
    { params }
  );
  return response.data;
}

/**
 * Add an observation
 */
export async function addObservation(
  patientUuid: string,
  data: Omit<CreateObservationData, 'patient_record_uuid'>
): Promise<FHIRResource> {
  const response = await apiClient.post(
    `${HEALTHCARE_BASE}/patients/${patientUuid}/add_observation/`,
    { ...data, patient_record_uuid: patientUuid }
  );
  return response.data;
}

/**
 * Add vital signs (convenience endpoint)
 */
export async function addVitalSigns(
  patientUuid: string,
  data: Omit<VitalSignsData, 'patient_record_uuid'>
): Promise<{
  count: number;
  observations: FHIRResource[];
}> {
  const response = await apiClient.post(
    `${HEALTHCARE_BASE}/patients/${patientUuid}/add_vital_signs/`,
    { ...data, patient_record_uuid: patientUuid }
  );
  return response.data;
}

// ==================== Conditions/Diagnoses ====================

/**
 * Get conditions for a patient
 */
export async function getPatientConditions(patientUuid: string): Promise<{
  count: number;
  conditions: FHIRResource[];
}> {
  const response = await apiClient.get(`${HEALTHCARE_BASE}/patients/${patientUuid}/conditions/`);
  return response.data;
}

/**
 * Add a condition/diagnosis
 */
export async function addCondition(
  patientUuid: string,
  data: Omit<CreateConditionData, 'patient_record_uuid'>
): Promise<FHIRResource> {
  const response = await apiClient.post(
    `${HEALTHCARE_BASE}/patients/${patientUuid}/add_condition/`,
    { ...data, patient_record_uuid: patientUuid }
  );
  return response.data;
}

/**
 * Update a condition's clinical status (e.g. active -> remission/inactive/
 * resolved). Same endpoint the Diagnósticos tab's own "Editar Diagnóstico"
 * modal uses.
 */
export async function updateConditionStatus(
  patientUuid: string,
  conditionId: string,
  data: { clinical_status?: string; verification_status?: string; notes?: string }
): Promise<{ success: boolean; condition: FHIRResource }> {
  const response = await apiClient.patch(
    `${HEALTHCARE_BASE}/patients/${patientUuid}/conditions/${conditionId}/`,
    data
  );
  return response.data;
}

// ==================== Documents ====================

/**
 * Get documents for a patient
 */
export async function getPatientDocuments(patientUuid: string): Promise<{
  count: number;
  documents: FHIRResource[];
}> {
  const response = await apiClient.get(`${HEALTHCARE_BASE}/patients/${patientUuid}/documents/`);
  return response.data;
}

/**
 * Add a clinical document
 */
export async function addDocument(
  patientUuid: string,
  data: Omit<CreateDocumentData, 'patient_record_uuid'>
): Promise<FHIRResource> {
  const { file, content_base64, ...rest } = data;

  if (file) {
    const formData = new FormData();
    formData.append('file', file);
    Object.entries(rest).forEach(([key, value]) => {
      if (value !== undefined && value !== null) formData.append(key, String(value));
    });
    const response = await apiClient.post(
      `${HEALTHCARE_BASE}/patients/${patientUuid}/add_document/`,
      formData,
      { headers: { 'Content-Type': undefined } }
    );
    return response.data;
  }

  const response = await apiClient.post(
    `${HEALTHCARE_BASE}/patients/${patientUuid}/add_document/`,
    { ...rest, content_base64, patient_record_uuid: patientUuid }
  );
  return response.data;
}

// ==================== Allergies ====================

/**
 * Get allergies for a patient
 */
export async function getPatientAllergies(patientUuid: string): Promise<{
  count: number;
  allergies: FHIRResource[];
}> {
  const response = await apiClient.get(`${HEALTHCARE_BASE}/patients/${patientUuid}/allergies/`);
  return response.data;
}

/**
 * Add an allergy
 */
export async function addAllergy(
  patientUuid: string,
  data: Omit<CreateAllergyData, 'patient_record_uuid'>
): Promise<FHIRResource> {
  const response = await apiClient.post(
    `${HEALTHCARE_BASE}/patients/${patientUuid}/add_allergy/`,
    { ...data, patient_record_uuid: patientUuid }
  );
  return response.data;
}

// ==================== Family History ====================

/**
 * Get family history for a patient
 */
export async function getPatientFamilyHistory(patientUuid: string): Promise<{
  count: number;
  family_history: FHIRResource[];
}> {
  const response = await apiClient.get(`${HEALTHCARE_BASE}/patients/${patientUuid}/family-history/`);
  return response.data;
}

/**
 * Add family history entry
 */
export async function addFamilyHistory(
  patientUuid: string,
  data: Omit<CreateFamilyHistoryData, 'patient_record_uuid'>
): Promise<FHIRResource> {
  const response = await apiClient.post(
    `${HEALTHCARE_BASE}/patients/${patientUuid}/add-family-history/`,
    { ...data, patient_record_uuid: patientUuid }
  );
  return response.data;
}

// ==================== Procedures ====================

/**
 * Get procedures for a patient
 */
export async function getPatientProcedures(patientUuid: string): Promise<{
  count: number;
  procedures: FHIRResource[];
}> {
  const response = await apiClient.get(`${HEALTHCARE_BASE}/patients/${patientUuid}/procedures/`);
  return response.data;
}

/**
 * Add a procedure
 */
export async function addProcedure(
  patientUuid: string,
  data: Omit<CreateProcedureData, 'patient_record_uuid'>
): Promise<FHIRResource> {
  const response = await apiClient.post(
    `${HEALTHCARE_BASE}/patients/${patientUuid}/add_procedure/`,
    { ...data, patient_record_uuid: patientUuid }
  );
  return response.data;
}

/**
 * Edit an existing procedure. Re-stamps who recorded the edit and adds a new
 * entry to the procedure's audit trail (see getProcedureHistory).
 */
export async function updateProcedure(
  patientUuid: string,
  procedureId: string,
  data: Partial<Omit<CreateProcedureData, 'patient_record_uuid'>>
): Promise<{ success: boolean; procedure: FHIRResource }> {
  const response = await apiClient.patch(
    `${HEALTHCARE_BASE}/patients/${patientUuid}/procedures/${procedureId}/`,
    data
  );
  return response.data;
}

export interface ProcedureHistoryEntry {
  name: string;
  recorded_at: string;
}

/**
 * Full audit trail for a procedure - one entry per create/edit, oldest first.
 */
export async function getProcedureHistory(
  patientUuid: string,
  procedureId: string
): Promise<{ count: number; history: ProcedureHistoryEntry[] }> {
  const response = await apiClient.get(
    `${HEALTHCARE_BASE}/patients/${patientUuid}/procedures/${procedureId}/history/`
  );
  return response.data;
}

/**
 * Upload an image/scanned document and attach it to a procedure. Accepts
 * JPG/PNG/WEBP/GIF/PDF, max 10MB.
 */
export async function uploadProcedureImage(
  patientUuid: string,
  procedureId: string,
  file: File
): Promise<FHIRResource> {
  const formData = new FormData();
  formData.append('file', file);
  const response = await apiClient.post(
    `${HEALTHCARE_BASE}/patients/${patientUuid}/procedures/${procedureId}/images/`,
    formData,
    { headers: { 'Content-Type': undefined } }
  );
  return response.data;
}

export interface ProcedureImage {
  id: string;
  title: string;
  content_type: string;
  url: string;
  date: string;
}

/**
 * Images/documents attached to a procedure. URLs are short-lived signed URLs,
 * regenerated fresh on every call - don't cache them for long.
 */
export async function getProcedureImages(
  patientUuid: string,
  procedureId: string
): Promise<{ count: number; images: ProcedureImage[] }> {
  const response = await apiClient.get(
    `${HEALTHCARE_BASE}/patients/${patientUuid}/procedures/${procedureId}/images/`
  );
  return response.data;
}

// ==================== Care Plans ====================

/**
 * Get care plans for a patient
 */
export async function getPatientCarePlans(patientUuid: string): Promise<{
  count: number;
  care_plans: FHIRResource[];
}> {
  const response = await apiClient.get(`${HEALTHCARE_BASE}/patients/${patientUuid}/care-plans/`);
  return response.data;
}

/**
 * Add a care plan
 */
export async function addCarePlan(
  patientUuid: string,
  data: Omit<CreateCarePlanData, 'patient_record_uuid'>
): Promise<FHIRResource> {
  const response = await apiClient.post(
    `${HEALTHCARE_BASE}/patients/${patientUuid}/add-care-plan/`,
    { ...data, patient_record_uuid: patientUuid }
  );
  return response.data;
}

// ==================== Clinical Impressions ====================

/**
 * Get clinical impressions for a patient
 */
export async function getPatientClinicalImpressions(patientUuid: string): Promise<{
  count: number;
  clinical_impressions: FHIRResource[];
}> {
  const response = await apiClient.get(`${HEALTHCARE_BASE}/patients/${patientUuid}/clinical-impressions/`);
  return response.data;
}

/**
 * Add a clinical impression (evolution note)
 */
export async function addClinicalImpression(
  patientUuid: string,
  data: Omit<CreateClinicalImpressionData, 'patient_record_uuid'>
): Promise<FHIRResource> {
  const response = await apiClient.post(
    `${HEALTHCARE_BASE}/patients/${patientUuid}/add-clinical-impression/`,
    { ...data, patient_record_uuid: patientUuid }
  );
  return response.data;
}

// ==================== Medication Statements ====================

/**
 * Get medication statements for a patient
 */
export async function getPatientMedicationStatements(patientUuid: string): Promise<{
  count: number;
  medication_statements: FHIRResource[];
}> {
  const response = await apiClient.get(`${HEALTHCARE_BASE}/patients/${patientUuid}/medication-statements/`);
  return response.data;
}

/**
 * Add a medication statement
 */
export async function addMedicationStatement(
  patientUuid: string,
  data: Omit<CreateMedicationStatementData, 'patient_record_uuid'>
): Promise<FHIRResource> {
  const response = await apiClient.post(
    `${HEALTHCARE_BASE}/patients/${patientUuid}/add-medication-statement/`,
    { ...data, patient_record_uuid: patientUuid }
  );
  return response.data;
}

// ==================== DICOM ====================

/**
 * Get DICOM studies for a patient
 */
export async function getPatientDICOMStudies(patientRecordUuid: string): Promise<DICOMStudyRecord[]> {
  const response = await apiClient.get(`${HEALTHCARE_BASE}/dicom/`, {
    params: { patient_record_uuid: patientRecordUuid }
  });
  // Handle both paginated response and direct array
  return Array.isArray(response.data) ? response.data : (response.data.results || []);
}

/**
 * Upload a DICOM study
 */
export async function uploadDICOMStudy(
  patientRecordUuid: string,
  file: File,
  metadata: {
    study_date: string;
    study_description?: string;
    modality: string;
    encounter_uuid?: string;
  }
): Promise<DICOMStudyRecord> {
  const formData = new FormData();
  formData.append('dicom_file', file);
  formData.append('patient_record_uuid', patientRecordUuid);
  formData.append('study_date', metadata.study_date);
  formData.append('modality', metadata.modality);
  if (metadata.study_description) {
    formData.append('study_description', metadata.study_description);
  }
  if (metadata.encounter_uuid) {
    formData.append('encounter_uuid', metadata.encounter_uuid);
  }

  const response = await apiClient.post(`${HEALTHCARE_BASE}/dicom/upload/`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
}

/**
 * Retrieve DICOM images for a study (rendered as base64 JPEGs via GCloud WADO-RS)
 */
export async function getDICOMRenderedFrames(studyUuid: string): Promise<{
  study_uid: string;
  study_description: string;
  modality: string;
  study_date: string;
  frames: { series_uid: string; instance_uid: string; data: string; content_type: string }[];
  total: number;
}> {
  const response = await apiClient.get(`${HEALTHCARE_BASE}/dicom/${studyUuid}/render_frames/`);
  return response.data;
}

/**
 * Retrieve DICOM images for a study
 */
export async function getDICOMStudyImages(studyUuid: string): Promise<any> {
  const response = await apiClient.get(`${HEALTHCARE_BASE}/dicom/${studyUuid}/retrieve_images/`);
  return response.data;
}

// ==================== DICOM volumetric/cine viewer (Part A backend actions) ====================

export type DICOMInstanceKind =
  | 'image'
  | 'multiframe'
  | 'video'
  | 'segmentation'
  | 'structured_report'
  | 'unsupported';

export interface DICOMManifestInstance {
  instance_uid: string;
  sop_class_uid: string;
  transfer_syntax_uid: string;
  frame_count: number;
  kind: DICOMInstanceKind;
  frame_rate: string | number | null;
  frame_of_reference_uid: string | null;
  slice_position: number | null;
  instance_number: number | null;
}

export interface DICOMManifestSeries {
  series_uid: string;
  total_instances: number;
  /** Already ordered along the acquisition axis by the backend when is_scrollable_stack is true. */
  instances: DICOMManifestInstance[];
  has_more_instances: boolean;
  /**
   * True when this series is the classic "one file per slice" CT/MR convention
   * (as opposed to a true multi-frame instance) -- multiple single-frame 'image'
   * instances sharing one FrameOfReferenceUID, already sorted by slice position.
   * The viewer should load all of them as one scrollable stack instead of paging
   * instance-by-instance.
   */
  is_scrollable_stack: boolean;
}

export interface DICOMStudyManifest {
  study_uid: string;
  study_description: string;
  modality: string;
  study_date: string;
  total_series: number;
  series: DICOMManifestSeries[];
  has_more_series: boolean;
}

/** Real per-instance classification (replaces the old all-frames-as-JPEG render_frames call). */
export async function getDICOMStudyManifest(
  studyUuid: string,
  params?: { series_offset?: number; series_limit?: number; instance_offset?: number; instance_limit?: number }
): Promise<DICOMStudyManifest> {
  const response = await apiClient.get(`${HEALTHCARE_BASE}/dicom/${studyUuid}/study_manifest/`, { params });
  return response.data;
}

/**
 * Binary endpoints require the same Authorization header as every other API call, which a
 * plain <img>/<video> src can't attach -- fetch as a blob via apiClient (so the auth
 * interceptor applies) and hand back an object URL for the element's src. Callers must
 * URL.revokeObjectURL() the result when done with it.
 */
async function fetchAsObjectUrl(url: string, params: Record<string, any>): Promise<string> {
  const response = await apiClient.get(url, { params, responseType: 'blob' });
  return URL.createObjectURL(response.data);
}

/** One rendered frame (1-based `frame`, default 1) of an instance -- flat-JPEG fallback, thumbnails, cine paging. */
export async function getDICOMFrameRenderedUrl(
  studyUuid: string,
  seriesUid: string,
  instanceUid: string,
  frame: number = 1
): Promise<string> {
  return fetchAsObjectUrl(`${HEALTHCARE_BASE}/dicom/${studyUuid}/frame_rendered/`, {
    series_uid: seriesUid,
    instance_uid: instanceUid,
    frame,
  });
}

/** Playable stream URL for an encapsulated-video instance, for a <video> element. */
export async function getDICOMVideoUrl(studyUuid: string, seriesUid: string, instanceUid: string): Promise<string> {
  return fetchAsObjectUrl(`${HEALTHCARE_BASE}/dicom/${studyUuid}/video/`, {
    series_uid: seriesUid,
    instance_uid: instanceUid,
  });
}

export interface DICOMSegmentationReference {
  referenced_series_uid: string | null;
  segment_count: number;
}

export async function getDICOMSegmentationReference(
  studyUuid: string,
  seriesUid: string,
  instanceUid: string
): Promise<DICOMSegmentationReference> {
  const response = await apiClient.get(`${HEALTHCARE_BASE}/dicom/${studyUuid}/segmentation/`, {
    params: { series_uid: seriesUid, instance_uid: instanceUid },
  });
  return response.data;
}

export interface DICOMStructuredReportNode {
  concept_name: string | null;
  value_type: string | null;
  value: any;
  children: DICOMStructuredReportNode[];
}

export interface DICOMStructuredReport {
  sop_instance_uid: string;
  document_title: string | null;
  completion_flag: string | null;
  verification_flag: string | null;
  root: DICOMStructuredReportNode | null;
}

export async function getDICOMStructuredReport(
  studyUuid: string,
  seriesUid: string,
  instanceUid: string
): Promise<DICOMStructuredReport> {
  const response = await apiClient.get(`${HEALTHCARE_BASE}/dicom/${studyUuid}/structured_report/`, {
    params: { series_uid: seriesUid, instance_uid: instanceUid },
  });
  return response.data;
}

/** Raw original DICOM file for an instance, as a Blob -- backs "download original". */
export async function downloadDICOMInstance(studyUuid: string, seriesUid: string, instanceUid: string): Promise<Blob> {
  const response = await apiClient.get(`${HEALTHCARE_BASE}/dicom/${studyUuid}/download_instance/`, {
    params: { series_uid: seriesUid, instance_uid: instanceUid },
    responseType: 'blob',
  });
  return response.data;
}

// ==================== Statistics ====================

/**
 * Get healthcare statistics
 */
export async function getHealthcareStats(params: {
  clinic_id?: number;
  professional_id?: number;
}): Promise<HealthcareStats> {
  const response = await apiClient.get(`${HEALTHCARE_BASE}/stats/`, { params });
  return response.data;
}

// ==================== Helper Functions ====================

/**
 * Format FHIR date to display string
 */
export function formatFHIRDate(dateString: string | null): string {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('es-HN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

/**
 * Calculate age from birth date
 */
export function calculateAge(birthDate: string | null): number | null {
  if (!birthDate) return null;
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
}

/**
 * Get gender display string
 */
export function getGenderDisplay(gender: string): string {
  const genderMap: Record<string, string> = {
    male: 'Masculino',
    female: 'Femenino',
    other: 'Otro',
    unknown: 'No especificado'
  };
  return genderMap[gender] || gender;
}

/**
 * Get encounter type display string
 */
export function getEncounterTypeDisplay(type: string): string {
  const typeMap: Record<string, string> = {
    AMB: 'Ambulatorio',
    EMER: 'Emergencia',
    IMP: 'HospitalizaciÃ³n',
    VR: 'Virtual'
  };
  return typeMap[type] || type;
}

/**
 * Get DICOM modality display string
 */
export function getModalityDisplay(modality: string): string {
  const modalityMap: Record<string, string> = {
    CR: 'RadiografÃ­a',
    CT: 'TomografÃ­a',
    MR: 'Resonancia',
    US: 'Ultrasonido',
    XA: 'AngiografÃ­a',
    NM: 'Medicina Nuclear',
    PT: 'PET',
    DX: 'RadiografÃ­a Digital',
    MG: 'MamografÃ­a',
    OT: 'Otro'
  };
  return modalityMap[modality] || modality;
}

// ==================== AI Clinical Assistant ====================

export interface AISessionStartParams {
  patient_fhir_id: string;
  professional_name?: string;
  specialty?: string;
  include_patient_context?: boolean;
}

export interface AISessionStartResponse {
  session_id: string;
  status: string;
  message: string;
  patient_context_loaded: boolean;
}

export interface AIChatMessageParams {
  session_id: string;
  message: string;
  include_references?: boolean;
}

export interface AIChatResponse {
  session_id: string;
  response: string;
  message_count: number;
  safety_ratings?: Array<{ category: string; probability: string }>;
}

export interface AIQuickSuggestion {
  title: string;
  content: string;
  priority: 'high' | 'medium' | 'low';
}

export interface AIDrugInteraction {
  medication1: string;
  medication2: string;
  severity: 'mild' | 'moderate' | 'severe';
  description: string;
  recommendation: string;
}

export interface AIDrugInteractionResponse {
  interactions: AIDrugInteraction[];
  overall_risk: 'low' | 'medium' | 'high';
  summary: string;
  alternatives: string[];
}

export interface AIICD10Suggestion {
  code: string;
  description: string;
  confidence: 'high' | 'medium' | 'low';
  notes: string;
}

export interface AIStatusResponse {
  vertex_ai_available: boolean;
  ai_initialized: boolean;
  ai_service_available: boolean;
  features: {
    clinical_chat: boolean;
    quick_suggestions: boolean;
    drug_interactions: boolean;
    icd10_suggestions: boolean;
  };
}

/**
 * Check AI service availability
 */
export async function getAIStatus(): Promise<AIStatusResponse> {
  const response = await apiClient.get(`${HEALTHCARE_BASE}/ai/status/`);
  return response.data;
}

/**
 * Start a new AI clinical assistant session
 */
export async function startAISession(params: AISessionStartParams): Promise<AISessionStartResponse> {
  const response = await apiClient.post(`${HEALTHCARE_BASE}/ai/session/start/`, params);
  return response.data;
}

/**
 * Send a message to the AI assistant
 */
export async function sendAIMessage(params: AIChatMessageParams): Promise<AIChatResponse> {
  const response = await apiClient.post(`${HEALTHCARE_BASE}/ai/session/message/`, params);
  return response.data;
}

/**
 * End an AI session
 */
export async function endAISession(sessionId: string): Promise<{ session_id: string; status: string }> {
  const response = await apiClient.post(`${HEALTHCARE_BASE}/ai/session/end/`, { session_id: sessionId });
  return response.data;
}

/**
 * Get AI session conversation history
 */
export async function getAISessionHistory(sessionId: string): Promise<{
  session_id: string;
  history: Array<{ role: 'user' | 'assistant'; content: string }>;
}> {
  const response = await apiClient.get(`${HEALTHCARE_BASE}/ai/session/${sessionId}/history/`);
  return response.data;
}

/**
 * Get quick AI suggestions for a patient
 */
export async function getAIQuickSuggestions(
  patientFhirId: string,
  consultationType: 'general' | 'followup' | 'emergency' = 'general'
): Promise<{ suggestions: AIQuickSuggestion[] }> {
  const response = await apiClient.post(`${HEALTHCARE_BASE}/ai/suggestions/`, {
    patient_fhir_id: patientFhirId,
    consultation_type: consultationType
  });
  return response.data;
}

/**
 * Analyze drug interactions
 */
export async function analyzeAIDrugInteractions(
  currentMedications: string[],
  proposedMedication: string
): Promise<AIDrugInteractionResponse> {
  const response = await apiClient.post(`${HEALTHCARE_BASE}/ai/drug-interactions/`, {
    current_medications: currentMedications,
    proposed_medication: proposedMedication
  });
  return response.data;
}

/**
 * Get ICD-10 code suggestions from AI
 */
export async function getAIICD10Suggestions(
  symptoms: string[],
  clinicalNotes?: string
): Promise<{ suggestions: AIICD10Suggestion[] }> {
  const response = await apiClient.post(`${HEALTHCARE_BASE}/ai/icd10-suggestions/`, {
    symptoms,
    clinical_notes: clinicalNotes
  });
  return response.data;
}

// ==================== Share Bundle (Remisión) ====================

export interface ShareBundleEncounterSnapshot {
  uuid: string;
  encounter_date: string;
  encounter_type: string;
  reason: string;
  symptoms: string;
  hea: string;
  physical_exam: string;
  treatment: string;
  status: string;
}

export interface ShareBundlePatientSnapshot {
  patient_name: string;
  patient_identifier: string;
  patient_birth_date: string | null;
  patient_gender: string;
  patient_phone: string;
  patient_email: string;
}

export interface ShareBundlePreview {
  uuid: string;
  sender_name: string;
  patient_snapshot: ShareBundlePatientSnapshot;
  encounters_snapshot: ShareBundleEncounterSnapshot[];
  labs_snapshot: Record<string, unknown>[];
  documents_snapshot: Record<string, unknown>[];
  dicom_studies: DICOMStudyRecord[];
  include_labs: boolean;
  include_documents: boolean;
  include_dicom: boolean;
  sender_note: string;
  status: 'pending' | 'viewed' | 'imported';
  expires_at: string;
  created_at: string;
}

export interface ShareBundleCreateData {
  patient_record_uuid: string;
  receiver_professional_id?: number;
  receiver_clinic_id?: number;
  encounter_uuids: string[];
  labs_snapshot: any[];
  documents_snapshot: any[];
  dicom_study_uuids: string[];
  sender_note?: string;
}

export interface ShareBundleInboxItem {
  uuid: string;
  token: string;
  sender_name: string;
  patient_name: string;
  recipient_name: string;
  status: 'pending' | 'viewed' | 'imported';
  created_at: string;
  expires_at: string;
  is_expired: boolean;
  include_labs: boolean;
  include_documents: boolean;
  include_dicom: boolean;
}

export async function createShareBundle(data: ShareBundleCreateData): Promise<{
  bundle_uuid: string;
  token: string;
  expires_at: string;
}> {
  const response = await apiClient.post(`${HEALTHCARE_BASE}/share/`, data);
  return response.data;
}

export async function getShareBundleInbox(): Promise<ShareBundleInboxItem[]> {
  const response = await apiClient.get(`${HEALTHCARE_BASE}/share/inbox/`);
  return response.data;
}

export interface ShareBundleSentItem {
  uuid: string;
  token: string;
  status: 'pending' | 'viewed' | 'imported';
  expires_at: string;
  created_at: string;
  viewed_at: string | null;
  imported_at: string | null;
  receiver_email: string;
  patient_name: string;
  recipient_name: string;
  is_expired: boolean;
  include_labs: boolean;
  include_documents: boolean;
  include_dicom: boolean;
}

export async function getSentShareBundles(): Promise<ShareBundleSentItem[]> {
  const response = await apiClient.get(`${HEALTHCARE_BASE}/share/sent/`);
  return response.data;
}

export async function getShareBundleClinicInbox(clinicId: number): Promise<ShareBundleInboxItem[]> {
  const response = await apiClient.get(`${HEALTHCARE_BASE}/share/clinic-inbox/${clinicId}/`);
  return response.data;
}

export async function getShareBundle(token: string): Promise<ShareBundlePreview> {
  const response = await apiClient.get(`${HEALTHCARE_BASE}/share/${token}/`);
  return response.data;
}

export async function importShareBundle(
  token: string,
  targetPatientRecordUuid?: string | null,
  clinicId?: number,
): Promise<{
  imported: boolean;
  patient_record_uuid: string;
  imported_encounters: number;
  imported_dicom: number;
  labs_note: string | null;
}> {
  const response = await apiClient.post(`${HEALTHCARE_BASE}/share/${token}/import/`, {
    target_patient_record_uuid: targetPatientRecordUuid ?? null,
    ...(clinicId ? { clinic_id: clinicId } : {}),
  });
  return response.data;
}

export async function transcribeAudio(audioBlob: Blob): Promise<{ transcript: string }> {
  const formData = new FormData();
  formData.append('audio', audioBlob, 'recording.webm');
  formData.append('mime_type', audioBlob.type || 'audio/webm');
  // Clear the instance-level 'application/json' default so axios auto-sets
  // 'multipart/form-data; boundary=...' from the FormData object
  const response = await apiClient.post(`${HEALTHCARE_BASE}/speech-to-text/`, formData, {
    headers: { 'Content-Type': undefined },
  });
  return response.data;
}

export default {
  getPatientRecords,
  getPatientRecord,
  createPatient,
  updatePatient,
  deletePatient,
  getPatientFullRecord,
  searchPatients,
  getPatientEncounters,
  createEncounter,
  updateEncounterStatus,
  getPatientObservations,
  addObservation,
  addVitalSigns,
  getPatientConditions,
  addCondition,
  getPatientDocuments,
  addDocument,
  getPatientAllergies,
  addAllergy,
  getPatientFamilyHistory,
  addFamilyHistory,
  getPatientProcedures,
  addProcedure,
  updateProcedure,
  getProcedureHistory,
  uploadProcedureImage,
  getProcedureImages,
  getPatientCarePlans,
  addCarePlan,
  getPatientClinicalImpressions,
  addClinicalImpression,
  getPatientMedicationStatements,
  addMedicationStatement,
  getPatientDICOMStudies,
  uploadDICOMStudy,
  getDICOMStudyImages,
  getHealthcareStats,
  formatFHIRDate,
  calculateAge,
  getGenderDisplay,
  getEncounterTypeDisplay,
  getModalityDisplay,
  // AI Clinical Assistant
  getAIStatus,
  startAISession,
  sendAIMessage,
  endAISession,
  getAISessionHistory,
  getAIQuickSuggestions,
  analyzeAIDrugInteractions,
  getAIICD10Suggestions,
  // Share Bundle (Remisión)
  createShareBundle,
  getShareBundleInbox,
  getShareBundle,
  importShareBundle,
};
